import { buildWords, replayCascade } from "@scripts/cascade";
import type { ComponentInit } from "@scripts/registry";

const CONTENT = "[data-fa-article-hero-content]";
/* Every article's image is already in the DOM with its own srcset — `Image` forwards
   `className` but not data-*, so the class is the only stable hook here. */
const IMAGES = ".fa-article-hero__frame .fa-article-hero__image";
const TAG = "[data-fa-article-hero-tag]";
const META = "[data-fa-article-hero-meta]";
const CTA = "[data-fa-article-hero-cta]";
const TILE_ROW = "[data-fa-article-hero-tile-row]";
const UP_NEXT_ROW = "[data-fa-article-hero-up-next-row]";
const TILE = "[data-fa-article-hero-tile]";
const TILE_FILL = "[data-fa-article-hero-tile-fill]";
const PREV = "[data-fa-article-hero-prev]";
const NEXT = "[data-fa-article-hero-next]";
const PAUSE = "[data-fa-article-hero-pause]";

const PAUSED = "data-paused";
const ACTIVE = "data-active";
const ACTIVE_IMAGE = "fa-article-hero__image--active";
const EXITING = "data-exiting";
const REVEAL = "data-fa-reveal";

type Article = {
	tag: string;
	title: string;
	href: string;
	date: string;
	dateText: string;
	readingTime: string;
};

const readArticle = (tile: HTMLElement): Article => ({
	tag: tile.getAttribute("data-fa-article-hero-tile-tag") ?? "",
	title: tile.getAttribute("data-fa-article-hero-tile-title-text") ?? "",
	href: tile.getAttribute("data-fa-article-hero-tile-href") ?? "",
	date: tile.getAttribute("data-fa-article-hero-tile-date") ?? "",
	dateText: tile.getAttribute("data-fa-article-hero-tile-date-text") ?? "",
	readingTime: tile.getAttribute("data-fa-article-hero-tile-reading-time") ?? "",
});

/* Mirrors Tag's own rendered shape — replaceChildren, not textContent, or the pill markup
   is gone after the first swap. */
const buildTag = (label: string): DocumentFragment => {
	const fragment = document.createDocumentFragment();

	const pill = document.createElement("span");
	pill.className = "fa-tag fa-tag--small";

	const text = document.createElement("span");
	text.className = "fa-text fa-text--caption fa-text--primary fa-text--strong";
	text.textContent = label;

	pill.append(text);
	fragment.append(pill);

	return fragment;
};

const buildMeta = (article: Article): DocumentFragment => {
	const fragment = document.createDocumentFragment();

	if (article.readingTime) {
		const span = document.createElement("span");
		span.className = "fa-text fa-text--sm-body fa-text--strong";
		span.textContent = article.readingTime;
		fragment.append(span);
	}

	if (article.readingTime && article.dateText) {
		const dot = document.createElement("span");
		dot.className = "fa-article-hero__dot";
		dot.setAttribute("aria-hidden", "true");
		fragment.append(dot);
	}

	if (article.dateText) {
		const span = document.createElement("span");
		span.className = "fa-text fa-text--sm-body fa-text--strong";

		const time = document.createElement("time");
		time.dateTime = article.date;
		time.textContent = article.dateText;

		span.append(time);
		fragment.append(span);
	}

	return fragment;
};

/* Toggling the attribute off/on (not just rewriting content) is what restarts the CSS entrance
   animation — `animation-fill-mode: both` freezes at its end state until the selector re-matches. */
const retrigger = (element: HTMLElement) => {
	element.removeAttribute(REVEAL);
	void element.offsetWidth;
	element.setAttribute(REVEAL, "fade-up");
};

export const initArticleHero: ComponentInit = (root) => {
	const content = root.querySelector<HTMLElement>(CONTENT);
	const images = [...root.querySelectorAll<HTMLElement>(IMAGES)];
	const tag = root.querySelector<HTMLElement>(TAG);
	const metas = [...root.querySelectorAll<HTMLElement>(META)];
	/* The block variant's reveal now sits on its wrapper — shared with the pause button next to
	   it — rather than on the meta text itself, so retriggering walks up to find it. */
	const metaReveals = metas.map((meta) => meta.closest<HTMLElement>(`[${REVEAL}]`) ?? meta);
	const cta = root.querySelector<HTMLAnchorElement>(CTA);
	const headline = root.querySelector<HTMLElement>(".fa-article-hero__headline");
	const tileRow = root.querySelector<HTMLElement>(TILE_ROW);
	const upNextRow = root.querySelector<HTMLElement>(UP_NEXT_ROW);
	const tiles = [...root.querySelectorAll<HTMLElement>(TILE)];
	const prev = root.querySelector<HTMLElement>(PREV);
	const next = root.querySelector<HTMLElement>(NEXT);
	const pauseButtons = [...root.querySelectorAll<HTMLButtonElement>(PAUSE)];
	const trackTiles = tileRow ? tiles.filter((tile) => tileRow.contains(tile)) : [];
	const upNextTiles = upNextRow ? tiles.filter((tile) => upNextRow.contains(tile)) : [];

	if (!content || images.length < 2 || !tag || !headline || !cta || trackTiles.length < 2) return;

	const authored = {
		content: content.innerHTML,
		imageClasses: images.map((image) => image.className),
		tag: tag.innerHTML,
		metas: metas.map((meta) => [meta, meta.innerHTML] as const),
		cta: { html: cta.innerHTML, href: cta.getAttribute("href") },
		headline: headline.textContent ?? "",
		active: trackTiles.findIndex((tile) => tile.hasAttribute(ACTIVE)),
		upNextRow: upNextRow?.innerHTML,
		pausePressed: pauseButtons.map(
			(button) => [button, button.getAttribute("aria-pressed")] as const,
		),
	};

	/* RevealWord renders with auto=false here (multiple articles), so it stamps neither
	   data-fa-cascade nor data-fa-word-reveal — set the former ourselves and remove it in
	   teardown [A11Y-3], since this script owns the split for as long as it is mounted. */
	headline.setAttribute("data-fa-cascade", "");
	headline.replaceChildren(buildWords(authored.headline));
	replayCascade(headline);

	const listeners = new AbortController();
	const { signal } = listeners;
	const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");

	let index = Math.max(authored.active, 0);

	/* Every upcoming tile is already rendered with its own correct content, so advancing is
	   just moving which one carries `data-active` — no rebuilding, unlike the rest of the hero. */
	const advanceUpNext = (toIndex: number) => {
		const from = upNextTiles.find((tile) => tile.hasAttribute(ACTIVE));
		const to = upNextTiles[toIndex];

		if (!to || to === from) return;

		to.removeAttribute(EXITING);
		to.inert = false;
		to.setAttribute(ACTIVE, "");

		if (from) {
			from.removeAttribute(ACTIVE);
			from.inert = true;
			from.setAttribute(EXITING, "");
		}
	};

	/* Without this an exited tile stays parked above rest, and its own next entrance would
	   transition down from there instead of up from below. */
	const settleUpNextExit = (event: TransitionEvent) => {
		if (event.propertyName !== "opacity") return;
		if (!(event.target instanceof HTMLElement)) return;

		event.target.removeAttribute(EXITING);
	};

	const activate = (target: number) => {
		const nextIndex = ((target % trackTiles.length) + trackTiles.length) % trackTiles.length;
		const tile = trackTiles[nextIndex];

		if (!tile) return;

		index = nextIndex;

		const article = readArticle(tile);

		tag.replaceChildren(buildTag(article.tag));
		retrigger(tag);

		for (const [position, meta] of metas.entries()) {
			meta.replaceChildren(buildMeta(article));
			retrigger(metaReveals[position]!);
		}

		headline.replaceChildren(buildWords(article.title));
		replayCascade(headline);

		cta.setAttribute("href", article.href);
		retrigger(cta);

		/* fa-hero-zoom restarts on its own — a CSS animation replays whenever its class newly
		   matches again, so no inline style is needed to force it. */
		const outgoing = images.find((candidate) => candidate.classList.contains(ACTIVE_IMAGE));
		const incoming = images[nextIndex];

		if (incoming && incoming !== outgoing) {
			outgoing?.classList.remove(ACTIVE_IMAGE);
			incoming.classList.add(ACTIVE_IMAGE);
		}

		for (const [position, candidate] of trackTiles.entries())
			candidate.toggleAttribute(ACTIVE, position === nextIndex);

		advanceUpNext((nextIndex + 1) % trackTiles.length);
	};

	/* Bound on `root`, not `tileRow`: below `lg` the row is `display:none` and its animation
	   never progresses, so only the up-next tile's identical, simultaneously-started fill
	   ever actually completes there. */
	const advance = (event: Event) => {
		if (!(event.target instanceof HTMLElement)) return;
		if (!event.target.matches(TILE_FILL)) return;

		activate(index + 1);
	};

	const select = (event: Event) => {
		const target = event.target;

		if (!(target instanceof Element)) return;

		const tile = target.closest<HTMLElement>(TILE);
		const position = tile ? trackTiles.indexOf(tile) : -1;

		if (position >= 0) activate(position);
	};

	const setPaused = (paused: boolean) => {
		root.toggleAttribute(PAUSED, paused);
		for (const button of pauseButtons) button.setAttribute("aria-pressed", String(paused));
	};

	tileRow?.addEventListener("click", select, { signal });
	upNextRow?.addEventListener("click", () => activate(index + 1), { signal });

	for (const button of pauseButtons)
		button.addEventListener("click", () => setPaused(!root.hasAttribute(PAUSED)), { signal });

	root.addEventListener("animationend", advance, { signal });
	root.addEventListener("transitionend", settleUpNextExit, { signal });

	prev?.addEventListener("click", () => activate(index - 1), { signal });
	next?.addEventListener("click", () => activate(index + 1), { signal });

	/* `data-fa-reveal`'s animation holds its end state indefinitely once done, outranking
	   `[data-exiting]`'s opacity forever unless cleared after this one entrance. */
	const upNextReveal = upNextTiles.find((tile) => tile.hasAttribute(REVEAL));
	if (upNextReveal) {
		upNextReveal.addEventListener("animationend", () => upNextReveal.removeAttribute(REVEAL), {
			once: true,
			signal,
		});
	}

	if (reduced.matches) setPaused(true);

	return () => {
		listeners.abort();

		if (upNextRow && authored.upNextRow !== undefined) upNextRow.innerHTML = authored.upNextRow;

		root.removeAttribute(PAUSED);

		for (const [button, pressed] of authored.pausePressed) {
			if (pressed === null) button.removeAttribute("aria-pressed");
			else button.setAttribute("aria-pressed", pressed);
		}

		content.innerHTML = authored.content;

		for (const [position, image] of images.entries())
			image.className = authored.imageClasses[position]!;

		tag.innerHTML = authored.tag;

		for (const [meta, html] of authored.metas) meta.innerHTML = html;

		cta.innerHTML = authored.cta.html;
		if (authored.cta.href === null) cta.removeAttribute("href");
		else cta.setAttribute("href", authored.cta.href);

		headline.removeAttribute("data-fa-cascade");
		headline.textContent = authored.headline;

		for (const [position, tile] of trackTiles.entries())
			tile.toggleAttribute(ACTIVE, position === authored.active);
	};
};
